import { useQueries } from '@fv/client-core'
import type { CarrierBookSettings, Workflow } from '@fv/models'

import { staticQueryOptions } from '@/hooks/useStaticDataQuery'
import { shipperFetch } from '@/utils/shipperFetch'

export const defaultCarrierBookSettings: CarrierBookSettings = {
  allowTLPostalCodeChange: true,
  requiresDeclaredValue: false,
  requiresFullBOLDetails: false,
  requireCustomsBrokerValue: false,
  requireOriginEmail: false,
  requiresFlatbedDetails: false,
}

export const carrierBookSettingsKeys = {
  all: ['carrier-book-settings'] as const,
  carrier: (workflow: Workflow, carrierId: string) =>
    [...carrierBookSettingsKeys.all, workflow, carrierId] as const,
}

async function fetchCarrierBookSettings(
  workflow: Workflow,
  carrierId: string,
): Promise<CarrierBookSettings> {
  return shipperFetch(`/book-settings/${carrierId}`, {
    query: { workflow },
  })
}

export function carrierBookSettingsQueryOptions(
  workflow: Workflow,
  carrierId: string,
) {
  return {
    ...staticQueryOptions,
    queryKey: carrierBookSettingsKeys.carrier(workflow, carrierId),
    queryFn: async () => {
      try {
        return await fetchCarrierBookSettings(workflow, carrierId)
      } catch {
        return defaultCarrierBookSettings
      }
    },
    retry: false,
  }
}

// May need settings for multiple carriers when booking multi-truck shipment.
export function useCarrierBookSettings(
  workflow: Workflow,
  carrierIds: string[],
) {
  const uniqueCarrierIds = Array.from(new Set(carrierIds))

  const settingsQueries = useQueries({
    queries: uniqueCarrierIds.map(id =>
      carrierBookSettingsQueryOptions(workflow, id),
    ),
  })

  const data: CarrierBookSettings = settingsQueries.reduce(
    (settings, q) => ({
      requiresDeclaredValue:
        q.data?.requiresDeclaredValue || settings.requiresDeclaredValue,
      requiresFullBOLDetails:
        q.data?.requiresFullBOLDetails || settings.requiresFullBOLDetails,
      allowTLPostalCodeChange:
        q.data?.allowTLPostalCodeChange ?? settings.allowTLPostalCodeChange,
      requireCustomsBrokerValue:
        q.data?.requireCustomsBrokerValue || settings.requireCustomsBrokerValue,
      requireOriginEmail:
        q.data?.requireOriginEmail || settings.requireOriginEmail,
      requiresFlatbedDetails:
        q.data?.requiresFlatbedDetails || settings.requiresFlatbedDetails,
    }),
    defaultCarrierBookSettings,
  )

  return {
    data,
    isLoading: settingsQueries.some(q => q.isLoading && q.isFetching),
  }
}
